import React, { useState, useEffect } from 'react';

/**
 * MarketCards - Animated market card display with selection highlighting
 *
 * @param {Array} markets - Array of market objects
 * @param {string} selectedId - The conditionId of the selected market
 * @param {function} onAnimationComplete - Callback when all animations finish
 * @param {number} maxDisplay - Maximum number of markets to display (default: 8)
 */
const MarketCards = ({ markets, selectedId, onAnimationComplete, maxDisplay = 8 }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [selectionComplete, setSelectionComplete] = useState(false);

  const displayMarkets = markets?.slice(0, maxDisplay) || [];

  // Stagger card appearance
  useEffect(() => {
    if (!displayMarkets.length) return;

    if (visibleCount < displayMarkets.length) {
      const timer = setTimeout(() => {
        setVisibleCount((prev) => prev + 1);
      }, 200); // 200ms between each card
      return () => clearTimeout(timer);
    } else if (visibleCount === displayMarkets.length && selectedId) {
      // All cards visible, now highlight selection after a pause
      const timer = setTimeout(() => {
        setSelectionComplete(true);
        onAnimationComplete?.();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [visibleCount, displayMarkets.length, selectedId, onAnimationComplete]);

  if (!displayMarkets.length) {
    return (
      <div className="market-cards market-cards--empty">
        <p className="market-cards__empty-text">Searching for markets...</p>
      </div>
    );
  }

  return (
    <div className="market-cards">
      <div className="market-cards__grid">
        {displayMarkets.map((market, index) => {
          const isVisible = index < visibleCount;
          const isSelected = market.conditionId === selectedId || market.id === selectedId;
          const isFaded = selectionComplete && !isSelected;

          return (
            <div
              key={market.conditionId || market.id || index}
              className={`market-card ${isVisible ? 'market-card--visible' : ''} ${isSelected && selectionComplete ? 'market-card--selected' : ''} ${isFaded ? 'market-card--faded' : ''}`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="market-card__type">
                {market.type === 'polymarket' ? 'PM' : 'EQ'}
              </div>
              <p className="market-card__question">
                {market.question || market.name}
              </p>
              {market.outcomePrices && (
                <div className="market-card__price">
                  {(() => {
                    try {
                      const prices = JSON.parse(market.outcomePrices);
                      return `${(prices[0] * 100).toFixed(0)}% Yes`;
                    } catch {
                      return market.outcomePrices;
                    }
                  })()}
                </div>
              )}
              {isSelected && selectionComplete && (
                <div className="market-card__selected-badge">Selected</div>
              )}
            </div>
          );
        })}
      </div>
      {selectionComplete && (
        <p className="market-cards__selection-note">
          Most relevant market identified
        </p>
      )}
    </div>
  );
};

export default MarketCards;
